import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { MainLayoutContainer } from "components/Layout";
import {
  useGetAllPostsQuery,
  useGetPostsByOrgQuery,
} from "graphql/__generated__/hooks";
import { useAppSelector } from "hooks/useAppSelector";
import { useAuthUser } from "hooks/useAuthUser";
import { FC, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import { TAG_COLOR } from "helpers/constants";
import dayjs from "dayjs";
import { AdminRoutes } from "config/routes";
import { ROLES } from "types/user";
import { audienceRoleLabel } from "./audienceRoleLabel";
import { PostAuthor } from "./PostAuthor";

type DateSort = "newest" | "oldest";

export const PostList: FC = () => {
  const { user } = useAuthUser();
  const isAdmin = user?.role === ROLES.ADMIN;
  const organization = useAppSelector(organizationSelectedSelector);
  const orgId = organization?.org_id;
  const [dateSort, setDateSort] = useState<DateSort>("newest");

  const orgPosts = useGetPostsByOrgQuery({
    variables: { orgId: orgId || "", limit: 10000 },
    skip: isAdmin || !orgId,
    fetchPolicy: "cache-and-network",
  });

  const allPosts = useGetAllPostsQuery({
    variables: { limit: 10000 },
    skip: !isAdmin,
    fetchPolicy: "cache-and-network",
  });

  const { data, loading, error } = isAdmin ? allPosts : orgPosts;

  const posts = useMemo(() => {
    const list = [...(data?.post || [])];
    list.sort((a, b) => {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return dateSort === "newest" ? bTime - aTime : aTime - bTime;
    });
    return list;
  }, [data?.post, dateSort]);

  return (
    <MainLayoutContainer
      title="Vet Discussions"
      buttonActions={
        <Button
          component={RouterLink}
          to={AdminRoutes.PostCreate}
          variant="contained"
          disabled={!orgId}
        >
          New Post
        </Button>
      }
    >
      {!isAdmin && !orgId && (
        <Alert severity="info">Select an organization to view discussions.</Alert>
      )}
      {error && (
        <Alert severity="error">Could not load discussion posts.</Alert>
      )}
      {loading && !data && <Typography>Loading...</Typography>}
      {(isAdmin || orgId) && !loading && !error && posts.length === 0 && (
        <Alert severity="info">No posts yet. Start a discussion!</Alert>
      )}
      {(isAdmin || orgId) && posts.length > 0 && (
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={dateSort}
            onChange={(_, value: DateSort | null) => {
              if (value) setDateSort(value);
            }}
            aria-label="Sort by date"
          >
            <ToggleButton value="newest">Newest first</ToggleButton>
            <ToggleButton value="oldest">Oldest first</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      <Stack spacing={2} mt={2}>
        {posts.map((post) => (
          <Card key={post.post_id} variant="outlined">
            <CardActionArea
              component={RouterLink}
              to={`/posts/${post.post_id}`}
              sx={{ alignItems: "stretch" }}
            >
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={1}
                  mb={1}
                >
                  <PostAuthor
                    name={post.author?.name}
                    pictureUrl={post.author?.picture_url}
                    equippedStoreItems={
                      (post.author as any)?.equipped_store_items
                    }
                    size={32}
                    nameVariant="body2"
                  />
                  {(post.tags || []).length > 0 && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                      justifyContent="flex-end"
                      sx={{ maxWidth: "40%", pt: 0.25 }}
                    >
                      {(post.tags || []).map((row) => (
                        <Chip
                          key={row.post_tag_id}
                          label={row.tag.name}
                          size="small"
                          variant="outlined"
                          color={TAG_COLOR(row.tag.type)}
                          sx={{
                            height: 20,
                            fontSize: "0.7rem",
                            opacity: 0.75,
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
                <Typography
                  variant="h6"
                  component="div"
                  color="primary"
                  fontWeight={600}
                >
                  {post.title}
                </Typography>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                  mt={0.5}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {(isAdmin || post.org_id !== orgId) &&
                      (post as any).organization?.name && (
                        <Typography variant="body2" color="text.secondary">
                          {(post as any).organization.name} ·
                        </Typography>
                      )}
                    <Typography variant="body2" color="text.secondary">
                      {dayjs(post.created_at).format("MMM D, YYYY")} ·
                    </Typography>
                    <ChatBubbleOutlineIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {post.answers_aggregate?.aggregate?.count || 0}
                    </Typography>
                  </Stack>
                  {audienceRoleLabel(post.audience_role) && (
                    <Chip
                      label={audienceRoleLabel(post.audience_role)}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        opacity: 0.75,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  )}
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </MainLayoutContainer>
  );
};
